/**
 * Utility functions for handling photo galleries
 */

/**
 * Attempts to load puppy gallery images based on puppy name
 * 
 * This function loads photos for a specific puppy by:
 * 1. Checking for a JSON index file in the puppy's gallery directory
 * 2. If not found, falling back to using the profile image
 * 
 * @param {Object} puppy - The puppy object containing name and image properties
 * @returns {Array} - Array of image paths for the puppy
 */
export const loadPuppyGalleryImages = (puppy) => {
  if (!puppy) return [];
  
  // Start with the profile image we know exists as a fallback
  const fallbackImages = [puppy.image];
  
  // Get the puppy name and convert to lowercase for the folder name
  const puppyName = puppy.name.toLowerCase();
  
  // Try to fetch the puppy's gallery index.json
  return fetch(`/dog-photos/puppy-gallery/${puppyName}/index.json`)
    .then(response => {
      if (!response.ok) {
        throw new Error('Puppy gallery index not found');
      }
      return response.json();
    })
    .then(data => {
      // Map the filenames to full paths and reverse so higher numbers display first
      // This makes adding new photos easier - just add with incrementing numbers
      return data.photos
        .map(photo => `/dog-photos/puppy-gallery/${puppyName}/${photo}`)
        .reverse();
    })
    .catch(error => {
      console.warn(`Could not load puppy gallery for ${puppyName}:`, error);
      return fallbackImages;
    });
};

/**
 * Extracts the puppy name from an image filename
 * 
 * @param {string} filename - The image filename
 * @returns {string|null} - The extracted puppy name or null if not found
 */
export const extractPuppyNameFromFilename = (filename) => {
  if (!filename) return null;
  
  // Extract the base filename without path
  const baseName = filename.split('/').pop();
  
  // Try to extract puppy name from patterns like "storm-profile.jpg"
  const match = baseName.match(/^([a-zA-Z]+)-/);
  if (match && match[1]) {
    return match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
  }
  
  return null;
};